import {
  CheckCircleOutlined,
  DeleteOutlined,
  EyeOutlined,
  LoginOutlined,
  StopOutlined,
  UnlockOutlined,
} from "@ant-design/icons";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { App, Button, Card, Drawer, Space, Tag, Tooltip, Typography } from "antd";
import type { TableProps } from "antd";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { ApiError, apiFetch, withQuery } from "../api/client";
import { compareText } from "../api/format";
import { useServerNotifications } from "../api/notifications";
import type {
  ManagedRoleListResponse,
  ManagedRoleRow,
  ManagedUserListResponse,
  ManagedUserRow,
  RoleActionResponse,
} from "../api/types";
import { useAuth } from "../auth/AuthProvider";
import { DataTable } from "../components/DataTable";
import { FilterBar } from "../components/FilterBar";
import type { FilterValues } from "../components/FilterBar";

/**
 * Sidebar entry "user_role_management" → /role/manage_roles.
 *
 * The users the caller may see, and the roles behind each of them. Who may do
 * what is the server's answer, not this screen's guess: the user list carries
 * `permissions`, the role list carries `permissions` plus an `editable` flag per
 * row.
 *
 * Every action here can end the caller's own session — suspending yourself,
 * removing your last role, or taking on another role's rights. The server acts
 * first and answers 200 with `login_required: true` and an already revoked
 * token, so the client only has to drop its local state; calling logout() would
 * fire a request with a token that no longer exists.
 */
export function RoleManagementPage() {
  const { t } = useTranslation();
  const notify = useServerNotifications();
  const { message, modal } = App.useApp();
  const queryClient = useQueryClient();
  const { endSession } = useAuth();

  const [filters, setFilters] = useState<FilterValues>({});
  const [rolesOf, setRolesOf] = useState<ManagedUserRow | null>(null);

  const users = useQuery({
    queryKey: ["users", filters],
    queryFn: () => apiFetch<ManagedUserListResponse>(withQuery("/users", filters)),
  });

  const showError = useCallback(
    (error: unknown) => {
      if (error instanceof ApiError && error.notifications) {
        notify(error.notifications);
        return;
      }
      void message.error(error instanceof Error ? error.message : String(error));
    },
    [notify, message],
  );

  /** Shared by both tables: run an action, then react to what came back. */
  const act = useMutation({
    mutationFn: ({ path, method }: { path: string; method: "PUT" | "DELETE" }) =>
      apiFetch<RoleActionResponse>(path, { method }),
    onSuccess: (data) => {
      notify(data.notifications);

      if (data.login_required) {
        // The token is already revoked on the server. Nothing else this screen
        // could do would succeed, so the session ends here.
        setRolesOf(null);
        endSession(t("user.new_login_needed"));
        return;
      }

      void queryClient.invalidateQueries({ queryKey: ["users"] });
      void queryClient.invalidateQueries({ queryKey: ["roles"] });
      // "Log in as" and access changes rewrite what the sidebar offers.
      void queryClient.invalidateQueries({ queryKey: ["sidebar"] });
    },
    onError: showError,
  });

  const confirmThen = useCallback(
    (question: string, run: () => void) => {
      modal.confirm({
        title: t("dialog.confirmation_title"),
        content: question,
        okText: t("dialog.answer_yes"),
        okButtonProps: { danger: true },
        cancelText: t("dialog.answer_no"),
        onOk: run,
      });
    },
    [modal, t],
  );

  const permissions = users.data?.permissions;

  // The five filters the old screen offered. The role types come from the
  // answer rather than from a list here: which ones a caller may filter by
  // depends on the roles they hold.
  const fields = useMemo(
    () => [
      { name: "first_name", label: t("activerecord.attributes.user.first_name") },
      { name: "last_name", label: t("activerecord.attributes.user.last_name") },
      { name: "congregation", label: t("activerecord.models.congregation") },
      { name: "circumscription", label: t("activerecord.models.circumscription") },
      {
        name: "roletype",
        label: t("activerecord.models.role"),
        options: users.data?.role_types ?? [],
      },
    ],
    [t, users.data],
  );

  const columns = useMemo<TableProps<ManagedUserRow>["columns"]>(
    () => [
      {
        title: t("activerecord.attributes.user.first_name"),
        dataIndex: "first_name",
        width: 160,
        defaultSortOrder: "ascend",
        sorter: (a, b) => compareText(a.first_name, b.first_name),
      },
      {
        title: t("activerecord.attributes.user.last_name"),
        dataIndex: "last_name",
        width: 160,
        sorter: (a, b) => compareText(a.last_name, b.last_name),
        render: (value: string | null, row) =>
          row.suspended ? (
            <Space size={4}>
              {value}
              <Tag color="orange">{t("activerecord.attributes.user.suspended")}</Tag>
            </Space>
          ) : (
            value
          ),
      },
      // Only the web master is given the login address, so the column is only
      // useful for them.
      ...(permissions?.manage_users
        ? [
            {
              title: t("activerecord.attributes.user.email"),
              dataIndex: "email",
              width: 220,
              ellipsis: true,
            },
          ]
        : []),
      {
        title: t("activerecord.attributes.user.private_email"),
        dataIndex: "private_email",
        width: 220,
        ellipsis: true,
      },
      {
        title: t("activerecord.attributes.user.phone"),
        dataIndex: "phone",
        width: 150,
      },
      {
        title: t("activerecord.attributes.user.mobile"),
        dataIndex: "mobile",
        width: 150,
      },
      {
        key: "actions",
        fixed: "right",
        width: 150,
        render: (_value, row) => (
          <Space size={4}>
            {permissions?.manage_users && (
              <>
                <Tooltip title={t("user.activate")}>
                  <Button
                    size="small"
                    icon={<CheckCircleOutlined />}
                    disabled={act.isPending || !row.suspended}
                    onClick={(event) => {
                      event.stopPropagation();
                      act.mutate({ path: `/users/${row.id}/activate`, method: "PUT" });
                    }}
                  />
                </Tooltip>
                <Tooltip title={t("user.suspend")}>
                  <Button
                    size="small"
                    icon={<StopOutlined />}
                    disabled={act.isPending || row.suspended}
                    onClick={(event) => {
                      event.stopPropagation();
                      confirmThen(t("user.confirm_suspend_user"), () =>
                        act.mutate({ path: `/users/${row.id}/suspend`, method: "PUT" }),
                      );
                    }}
                  />
                </Tooltip>
                <Tooltip title={t("user.login_as")}>
                  <Button
                    size="small"
                    icon={<LoginOutlined />}
                    disabled={act.isPending}
                    onClick={(event) => {
                      event.stopPropagation();
                      act.mutate({ path: `/users/${row.id}/login_as`, method: "PUT" });
                    }}
                  />
                </Tooltip>
              </>
            )}
            {permissions?.remove_user && (
              <Tooltip title={t("user.remove")}>
                <Button
                  size="small"
                  danger
                  icon={<DeleteOutlined />}
                  disabled={act.isPending}
                  onClick={(event) => {
                    event.stopPropagation();
                    confirmThen(t("user.confirm_remove_user"), () =>
                      act.mutate({ path: `/users/${row.id}`, method: "DELETE" }),
                    );
                  }}
                />
              </Tooltip>
            )}
          </Space>
        ),
      },
    ],
    [t, permissions, act, confirmThen],
  );

  return (
    <Card title={t("role.management")}>
      <FilterBar fields={fields} onApply={setFilters} loading={users.isFetching} />

      <DataTable<ManagedUserRow>
        columns={columns}
        rows={users.data?.data}
        exportAs={t("role.management")}
        loading={users.isPending || act.isPending}
        error={users.error}
        onRowClick={setRolesOf}
      />

      {rolesOf && (
        <RolesDrawer
          user={rolesOf}
          onClose={() => setRolesOf(null)}
          onAct={(path, method) => act.mutate({ path, method })}
          busy={act.isPending}
          confirmThen={confirmThen}
        />
      )}
    </Card>
  );
}

/** The roles of one user. Clicking a row on the left opens this. */
function RolesDrawer({
  user,
  onClose,
  onAct,
  busy,
  confirmThen,
}: {
  user: ManagedUserRow;
  onClose: () => void;
  onAct: (path: string, method: "PUT" | "DELETE") => void;
  busy: boolean;
  confirmThen: (question: string, run: () => void) => void;
}) {
  const { t } = useTranslation();

  const roles = useQuery({
    queryKey: ["roles", user.id],
    queryFn: () => apiFetch<ManagedRoleListResponse>(`/users/${user.id}/roles`),
  });

  const rows = roles.data?.data ?? [];
  const manage = roles.data?.permissions.manage_roles ?? false;

  const columns = useMemo<TableProps<ManagedRoleRow>["columns"]>(
    () => [
      {
        title: t("activerecord.attributes.role.roletype"),
        dataIndex: "roletype_name",
        width: 180,
      },
      {
        title: t("activerecord.models.circumscription"),
        dataIndex: "circumscription",
        width: 160,
        ellipsis: true,
      },
      {
        title: t("activerecord.models.congregation"),
        dataIndex: "congregation",
        width: 180,
        ellipsis: true,
      },
      {
        title: t("activerecord.attributes.role.observer"),
        dataIndex: "observer",
        width: 120,
        render: (value: boolean) => (value ? <Tag>{t("role.observer")}</Tag> : null),
      },
      {
        title: t("activerecord.attributes.role.suspended"),
        dataIndex: "suspended",
        width: 120,
        render: (value: boolean) => (value ? <Tag color="orange">{t("yes")}</Tag> : null),
      },
      {
        key: "actions",
        fixed: "right",
        width: 210,
        render: (_value, row) => {
          if (!row.editable) return null;

          // Removing the last role takes the account with it, which is a
          // different question from removing one of several.
          const lastRole = rows.length === 1;

          return (
            <Space size={4}>
              {manage && (
                <Tooltip title={t("role.activate")}>
                  <Button
                    size="small"
                    icon={<CheckCircleOutlined />}
                    disabled={busy || !row.suspended}
                    onClick={() => onAct(`/users/${user.id}/roles/${row.id}/activate`, "PUT")}
                  />
                </Tooltip>
              )}
              <Tooltip title={t("role.suspend")}>
                <Button
                  size="small"
                  icon={<StopOutlined />}
                  disabled={busy || row.suspended}
                  onClick={() =>
                    confirmThen(t("role.confirm_suspend_role"), () =>
                      onAct(`/users/${user.id}/roles/${row.id}/suspend`, "PUT"),
                    )
                  }
                />
              </Tooltip>
              {manage && (
                <Tooltip title={t("role.full_access")}>
                  <Button
                    size="small"
                    icon={<UnlockOutlined />}
                    disabled={busy || !row.observer}
                    onClick={() => onAct(`/users/${user.id}/roles/${row.id}/full_access`, "PUT")}
                  />
                </Tooltip>
              )}
              <Tooltip title={t("role.observer")}>
                <Button
                  size="small"
                  icon={<EyeOutlined />}
                  disabled={busy || row.observer}
                  onClick={() => onAct(`/users/${user.id}/roles/${row.id}/observer`, "PUT")}
                />
              </Tooltip>
              {manage && (
                <Tooltip title={t("role.login_as")}>
                  <Button
                    size="small"
                    icon={<LoginOutlined />}
                    disabled={busy}
                    onClick={() => onAct(`/users/${user.id}/roles/${row.id}/login_as`, "PUT")}
                  />
                </Tooltip>
              )}
              <Tooltip title={t("role.remove")}>
                <Button
                  size="small"
                  danger
                  icon={<DeleteOutlined />}
                  disabled={busy}
                  onClick={() =>
                    confirmThen(
                      lastRole
                        ? t("role.confirm_remove_role_and_account")
                        : t("role.confirm_remove_role"),
                      () => onAct(`/users/${user.id}/roles/${row.id}`, "DELETE"),
                    )
                  }
                />
              </Tooltip>
            </Space>
          );
        },
      },
    ],
    [t, rows.length, manage, busy, user.id, onAct, confirmThen],
  );

  return (
    <Drawer
      open
      onClose={onClose}
      size={980}
      destroyOnHidden
      title={
        <Typography.Text>
          {[user.first_name, user.last_name].filter(Boolean).join(" ")}
        </Typography.Text>
      }
    >
      <DataTable<ManagedRoleRow>
        columns={columns}
        rows={rows}
        exportAs={[user.first_name, user.last_name, t("role.management")]
          .filter(Boolean)
          .join(" · ")}
        loading={roles.isPending || busy}
        error={roles.error}
        paginated={false}
      />
    </Drawer>
  );
}
